import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DragIndicator from "@mui/icons-material/DragIndicator";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Slider,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Dayjs } from "dayjs";
import { motion } from "framer-motion";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { sorted } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useScaleWeights } from "hooks/useScaleWeights";
import {
  laneColorFor,
  timelineGutterWidth,
  timelineHeight,
} from "./LoadingWindowTimeline";
import { createTimeScale, timeAxisTicks } from "./timeScale";
import { clipTrace, niceCeil, traceScaleWeights } from "./traceSegments";

const CHART_HEIGHT = 180;
const CHART_PAD_TOP = 14;
// The plot floor: traces and gridlines end on a baseline here, so the chart
// visibly terminates before the paper edge.
const CHART_PAD_BOTTOM = 26;
// Every 5th sample keeps the padded day's payload modest while individual
// lifts stay visible.
const DOWNSAMPLE = 5;
const MAX_POINTS_PER_TRACE = 2000;
const PLOT_HEIGHT = CHART_HEIGHT - CHART_PAD_TOP - CHART_PAD_BOTTOM;

export const DEFAULT_TRACE_BG_ALPHA = 0.45;

export interface MassTraceOverlayProps {
  readonly tenantName: string;
  readonly buckets: readonly string[];
  readonly domainStartMs: number;
  readonly domainEndMs: number;
  readonly periodStart: Dayjs;
  readonly periodEnd: Dayjs;
  readonly initialAreas: readonly string[];
  // Owned by the page so the choices survive closing and reopening the strip.
  readonly selectedAreas: ReadonlySet<string> | null;
  readonly onSelectedAreasChange: (areas: ReadonlySet<string>) => void;
  readonly bgAlpha: number;
  readonly onBgAlphaChange: (bgAlpha: number) => void;
  readonly cursorMs?: number | null;
  readonly onClose: () => void;
}

// A translucent scale-weight trace floated over the timeline. The x-axis is
// locked to the timeline's scale (same domain, width and gutter) so weight
// steps stay aligned with windows through pan/zoom; only y-position is free so
// the strip can be dragged next to the lane under review. Pointer events stay
// off everywhere except the header, so windows remain editable through it.
export const MassTraceOverlay = ({
  tenantName,
  buckets,
  domainStartMs,
  domainEndMs,
  periodStart,
  periodEnd,
  initialAreas,
  selectedAreas,
  onSelectedAreasChange,
  bgAlpha,
  onBgAlphaChange,
  cursorMs,
  onClose,
}: MassTraceOverlayProps) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const { format } = useNumberSerialiser();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [widthPx, setWidthPx] = useState(0);
  const clipId = useId();
  // The drag is tracked locally so each tick re-renders only this strip; the
  // page state (and everything under it) updates once, on release.
  const [liveAlpha, setLiveAlpha] = useState(bgAlpha);

  useEffect(() => {
    const element = svgRef.current;
    if (element === null) return;
    const observer = new ResizeObserver((entries) => {
      setWidthPx(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  const { series, isFetching, isError } = useScaleWeights({
    tenantName,
    start: periodStart,
    end: periodEnd,
    downsample: DOWNSAMPLE,
  });

  const areas = useMemo(
    () =>
      sorted([...new Set(series.map((point) => point.area))], (area) => area),
    [series]
  );

  const activeAreas = useMemo((): ReadonlySet<string> => {
    if (selectedAreas !== null) return selectedAreas;
    // Lanes in view may have no scale data; defaulting to them would render
    // an empty chart that reads as a scale outage.
    const withData = initialAreas.filter((area) => areas.includes(area));
    if (withData.length > 0) return new Set(withData);
    return new Set(areas);
  }, [selectedAreas, initialAreas, areas]);

  const toggleArea = (area: string) => {
    const next = new Set(activeAreas);
    if (next.has(area)) next.delete(area);
    else next.add(area);
    onSelectedAreasChange(next);
  };

  // Keyed by lane order (not by areas-with-data) so a car keeps its colour
  // across periods and matches the timeline's gutter dots.
  const colorForArea = useMemo(
    () =>
      new Map(areas.map((area) => [area, laneColorFor(theme, buckets, area)])),
    [areas, buckets, theme]
  );

  const tracesByArea = useMemo(() => traceScaleWeights(series), [series]);

  const gutterWidth = timelineGutterWidth(buckets);
  const scale = useMemo(
    () =>
      createTimeScale({
        domainStartMs,
        domainEndMs,
        width: Math.max(widthPx - gutterWidth, 1),
      }),
    [domainStartMs, domainEndMs, widthPx, gutterWidth]
  );
  const ticks = useMemo(() => timeAxisTicks(scale, 8), [scale]);

  const visibleTraces = useMemo(
    () =>
      [...tracesByArea.entries()]
        .filter(([area]) => activeAreas.has(area))
        .flatMap(([, trace]) => {
          const clipped = clipTrace(
            trace,
            domainStartMs,
            domainEndMs,
            MAX_POINTS_PER_TRACE
          );
          return clipped === null ? [] : [clipped];
        }),
    [tracesByArea, activeAreas, domainStartMs, domainEndMs]
  );

  const yMax = useMemo(
    () =>
      niceCeil(Math.max(0, ...visibleTraces.flatMap((trace) => trace.values))),
    [visibleTraces]
  );

  const yFor = useCallback(
    (value: number): number =>
      CHART_HEIGHT -
      CHART_PAD_BOTTOM -
      (Math.min(value, yMax) / yMax) * PLOT_HEIGHT,
    [yMax]
  );

  // Pre-built polyline geometry: thousands of points must not be restrung on
  // renders that change only styling (opacity drag, video cursor ticks).
  const renderedTraces = useMemo(
    () =>
      visibleTraces.map((trace) => ({
        area: trace.area,
        points: trace.timesMs
          .map(
            (ms, i) =>
              `${gutterWidth + scale.timeToX(ms)},${yFor(trace.values[i] ?? 0)}`
          )
          .join(" "),
      })),
    [visibleTraces, scale, gutterWidth, yFor]
  );

  const empty = !isFetching && !isError && visibleTraces.length === 0;

  return (
    <motion.div
      drag="y"
      dragMomentum={false}
      dragElastic={0}
      dragConstraints={{ top: -16, bottom: timelineHeight(buckets) }}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        pointerEvents: "none",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.background.paper, liveAlpha),
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            px: 1,
            py: 0.25,
            bgcolor: alpha(
              theme.palette.background.paper,
              Math.min(liveAlpha + 0.15, 1)
            ),
            cursor: "grab",
            pointerEvents: "auto",
          }}
        >
          <DragIndicator sx={{ fontSize: 16, opacity: 0.5 }} />
          <Typography variant="body2" sx={{ fontWeight: "fontWeightMedium" }}>
            {t("scaleWeight")}
          </Typography>
          {areas.map((area) => {
            const on = activeAreas.has(area);
            const color = colorForArea.get(area) ?? theme.palette.primary.main;
            return (
              <Chip
                key={area}
                size="small"
                label={area}
                variant={on ? "filled" : "outlined"}
                onClick={() => {
                  toggleArea(area);
                }}
                icon={
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: color,
                      ml: 0.75,
                    }}
                  />
                }
                sx={{
                  bgcolor: on ? alpha(color, 0.15) : undefined,
                  borderColor: color,
                }}
              />
            );
          })}
          {isFetching ? <CircularProgress size={14} /> : null}
          {isError ? (
            <Typography variant="caption" color="error">
              {t("failedToLoadData")}
            </Typography>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <Tooltip title={t("opacity")} arrow>
            <Slider
              aria-label={t("opacity")}
              size="small"
              value={liveAlpha}
              min={0.15}
              max={1}
              step={0.05}
              onChange={(_, value) => {
                setLiveAlpha(value);
              }}
              onChangeCommitted={(_, value) => {
                onBgAlphaChange(value);
              }}
              // The strip is draggable by its header; keep slider drags local.
              onPointerDownCapture={(event) => {
                event.stopPropagation();
              }}
              sx={{ width: 90, mx: 1, flexShrink: 0 }}
            />
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseRoundedIcon fontSize="inherit" />
          </IconButton>
        </Stack>

        <svg
          ref={svgRef}
          width="100%"
          height={CHART_HEIGHT}
          style={{ display: "block", pointerEvents: "none" }}
        >
          <defs>
            <clipPath id={clipId}>
              <rect x={gutterWidth} y={0} width="100%" height={CHART_HEIGHT} />
            </clipPath>
          </defs>

          {ticks.map((tick) => (
            <line
              key={tick.ms}
              x1={gutterWidth + tick.x}
              x2={gutterWidth + tick.x}
              y1={0}
              y2={CHART_HEIGHT - CHART_PAD_BOTTOM}
              stroke={theme.palette.grey[200]}
            />
          ))}

          {[0, 0.5, 1].map((fraction) => (
            <g key={fraction}>
              <line
                x1={gutterWidth}
                x2="100%"
                y1={yFor(fraction * yMax)}
                y2={yFor(fraction * yMax)}
                stroke={
                  fraction === 0
                    ? theme.palette.grey[400]
                    : theme.palette.grey[300]
                }
                strokeDasharray={fraction === 0 ? undefined : "2 3"}
              />
              <text
                x={gutterWidth - 6}
                y={yFor(fraction * yMax) + 3}
                textAnchor="end"
                fill={theme.palette.text.secondary}
                fontSize={9}
                fontFamily={theme.typography.body2Mono.fontFamily}
              >
                {`${format(fraction * yMax, {
                  decimalPlaces: Number.isInteger(fraction * yMax) ? 0 : 1,
                })} t`}
              </text>
            </g>
          ))}

          {renderedTraces.map(({ area, points }) => (
            <polyline
              key={area}
              points={points}
              fill="none"
              stroke={colorForArea.get(area) ?? theme.palette.primary.main}
              strokeWidth={1.5}
              opacity={0.9}
              clipPath={`url(#${clipId})`}
            />
          ))}

          {cursorMs != null &&
          cursorMs >= domainStartMs &&
          cursorMs <= domainEndMs ? (
            <line
              x1={gutterWidth + scale.timeToX(cursorMs)}
              x2={gutterWidth + scale.timeToX(cursorMs)}
              y1={0}
              y2={CHART_HEIGHT}
              stroke={theme.palette.error.main}
              strokeWidth={1.5}
              clipPath={`url(#${clipId})`}
            />
          ) : null}

          {empty ? (
            <text
              x="50%"
              y={CHART_HEIGHT / 2}
              textAnchor="middle"
              fill={theme.palette.text.secondary}
              fontSize={11}
            >
              {t("noScaleWeightData")}
            </text>
          ) : null}
        </svg>
      </Paper>
    </motion.div>
  );
};
